<template>
    <div class="modal fade" id="stock_movement_modal" ref="modalEl" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-560px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.stock.movement.addTitle') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>

                <el-form @submit.prevent="onSubmit" :model="form" ref="formRef">
                    <div class="modal-body py-8 px-lg-10">
                        <!-- Type segment -->
                        <div class="smd-field">
                            <label class="smd-label">{{ $t('pages.stock.movement.type') }}</label>
                            <div class="smd-segment">
                                <button type="button"
                                    class="smd-segment-btn"
                                    :class="{'smd-segment-btn--active smd-segment-btn--in': form.movement_type === 1}"
                                    @click="setType(1)">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                                        <line x1="12" y1="19" x2="12" y2="5"/>
                                        <polyline points="5 12 12 5 19 12"/>
                                    </svg>
                                    {{ $t('pages.stock.movement.typeLabel.1') }}
                                </button>
                                <button type="button"
                                    class="smd-segment-btn"
                                    :class="{'smd-segment-btn--active smd-segment-btn--out': form.movement_type === 2}"
                                    @click="setType(2)">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <polyline points="19 12 12 19 5 12"/>
                                    </svg>
                                    {{ $t('pages.stock.movement.typeLabel.2') }}
                                </button>
                                <button type="button"
                                    class="smd-segment-btn"
                                    :class="{'smd-segment-btn--active smd-segment-btn--adj': form.movement_type === 3}"
                                    @click="setType(3)">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M3 6h18M3 12h18M3 18h18"/>
                                    </svg>
                                    {{ $t('pages.stock.movement.typeLabel.3') }}
                                </button>
                            </div>
                            <span v-if="form.movement_type === 3" class="smd-hint smd-hint--warn">
                                {{ $t('pages.stock.movement.adjustmentWarn') }}
                            </span>
                        </div>

                        <!-- Location -->
                        <div class="smd-field">
                            <label class="smd-label smd-label--required">{{ $t('pages.stock.movement.location') }}</label>
                            <el-select v-model="form.stock_location_id" class="w-100"
                                :placeholder="$t('pages.stock.movement.locationPlaceholder')">
                                <el-option v-for="loc in locations" :key="loc.id" :value="loc.id"
                                    :label="locationLabel(loc)"/>
                            </el-select>
                        </div>

                        <!-- Quantity + Unit Price -->
                        <div class="smd-grid">
                            <div class="smd-field">
                                <label class="smd-label smd-label--required">{{ $t('pages.stock.movement.quantity') }}</label>
                                <el-input v-model="form.quantity"
                                    oninput="value=value.replace(/[^0-9.]/g,'')"
                                    :placeholder="$t('pages.stock.movement.quantityPlaceholder')"/>
                            </div>
                            <div class="smd-field">
                                <label class="smd-label">{{ $t('pages.stock.movement.unitPrice') }}</label>
                                <el-input v-model="form.unit_price"
                                    oninput="value=value.replace(/[^0-9.]/g,'')"
                                    placeholder="0,00"/>
                            </div>
                        </div>

                        <!-- IN + is_expense toggle (sadece IN tipinde) -->
                        <div v-if="form.movement_type === 1" class="smd-field">
                            <label class="smd-toggle-wrap">
                                <input type="checkbox" v-model="form.is_expense"/>
                                <span class="smd-toggle-pill"></span>
                                <span class="smd-toggle-text">
                                    <span class="smd-toggle-title">{{ $t('pages.stock.movement.isExpense') }}</span>
                                    <span class="smd-toggle-hint">{{ $t('pages.stock.movement.isExpenseHint') }}</span>
                                </span>
                            </label>
                        </div>

                        <!-- is_expense aktifse: kasa + ödeme yöntemi -->
                        <template v-if="form.movement_type === 1 && form.is_expense">
                            <div class="smd-grid">
                                <div class="smd-field">
                                    <label class="smd-label">{{ $t('pages.stock.movement.paymentMethod') }}</label>
                                    <el-select v-model="form.payment_method_id" class="w-100" clearable
                                        :placeholder="$t('common.transaction.payment.methodPlaceholder')">
                                        <el-option v-for="m in paymentMethods" :key="m.id" :value="m.id" :label="methodLabel(m)"/>
                                    </el-select>
                                </div>
                                <div class="smd-field">
                                    <label class="smd-label">{{ $t('pages.stock.movement.cashRegister') }}</label>
                                    <el-select v-model="form.cash_register_id" class="w-100" clearable
                                        :placeholder="$t('common.transaction.payment.cashRegisterPlaceholder')">
                                        <el-option v-for="c in cashRegisters" :key="c.id"
                                            :value="c.id"
                                            :label="c.name + (c.is_default ? ' ' + $t('pages.stock.movement.cashRegisterDefault') : '')"/>
                                    </el-select>
                                </div>
                            </div>
                        </template>

                        <!-- Note -->
                        <div class="smd-field">
                            <label class="smd-label">{{ $t('pages.stock.movement.note') }}</label>
                            <el-input v-model="form.note" type="textarea" :rows="2"
                                :placeholder="$t('pages.stock.movement.notePlaceholder')"/>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="submit" class="btn btn-primary"
                            :disabled="!canSubmit || loading"
                            :data-kt-indicator="loading ? 'on' : null">
                            <span v-if="!loading" class="indicator-label">{{ $t('common.save') }}</span>
                            <span v-if="loading" class="indicator-progress">
                                {{ $t('messages.wait') }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import transactionService from "@/components/payment/transactionService";

function emptyForm() {
    return {
        movement_type: 1,
        stock_location_id: null,
        quantity: '',
        unit_price: '',
        is_expense: true,
        payment_method_id: null,
        cash_register_id: null,
        note: '',
    };
}

export default {
    name: "StockMovementDialog",
    props: {
        stockId: { type: [Number, String], required: true },
        locations: { type: Array, default: () => [] },
    },
    emits: ['created'],
    data() {
        return {
            form: emptyForm(),
            loading: false,
        };
    },
    computed: {
        paymentMethods() { return transactionService.state.paymentMethods; },
        cashRegisters() { return transactionService.state.cashRegisters; },
        canSubmit() {
            const qty = parseFloat(this.form.quantity);
            if (!(qty > 0)) return false;
            if (!this.form.stock_location_id) return false;
            return true;
        },
    },
    methods: {
        locationLabel(loc) {
            const name = loc.warehouse?.name || ('Depo #' + loc.warehouse_id);
            const qty = parseFloat(loc.current_quantity) || 0;
            return `${name} (mevcut: ${qty})`;
        },
        methodLabel(m) {
            const key = 'common.transaction.payment.methodCode.' + m.code;
            const t = this.$t(key);
            return t === key ? m.name : t;
        },
        setType(t) {
            this.form.movement_type = t;
            if (t !== 1) this.form.is_expense = false;
        },
        async open(preset = {}) {
            this.form = emptyForm();
            if (this.locations.length === 1) {
                this.form.stock_location_id = this.locations[0].id;
            }
            if (preset.movement_type) this.form.movement_type = preset.movement_type;
            if (preset.stock_location_id) this.form.stock_location_id = preset.stock_location_id;
            if (this.form.movement_type !== 1) this.form.is_expense = false;
            transactionService.ensurePaymentMethods();
            await transactionService.ensureCashRegisters();
            const def = transactionService.state.cashRegisters.find(c => c.is_default);
            if (def) this.form.cash_register_id = def.id;
            const cash = transactionService.state.paymentMethods.find(m => m.code === 'CASH');
            if (cash) this.form.payment_method_id = cash.id;
            this.showModal(this.$refs.modalEl);
        },
        async onSubmit() {
            if (!this.canSubmit) return;
            this.loading = true;
            const body = {
                stock_location_id: this.form.stock_location_id,
                movement_type: this.form.movement_type,
                quantity: parseFloat(this.form.quantity),
                unit_price: parseFloat(this.form.unit_price) || 0,
            };
            if (this.form.movement_type === 1) {
                body.is_expense = !!this.form.is_expense;
                if (body.is_expense) {
                    if (this.form.payment_method_id) body.payment_method_id = this.form.payment_method_id;
                    if (this.form.cash_register_id) body.cash_register_id = this.form.cash_register_id;
                }
            } else {
                body.is_expense = false;
            }
            if (this.form.note) body.note = this.form.note;

            try {
                const res = await this.$store.dispatch('stock/movement/add', {
                    stockId: this.stockId,
                    formData: body,
                });
                if (res?.status) {
                    this.$message.success(this.$t('messages.saveOk'));
                    this.hideModal(this.$refs.modalEl);
                    this.$emit('created', res.data);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.smd-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
}

.smd-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 0;
}

@media (max-width: 580px) {
    .smd-grid { grid-template-columns: 1fr; }
}

.smd-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.smd-label--required::after {
    content: ' *';
    color: #ef4444;
}

.smd-hint {
    font-size: 11.5px;
    color: #8e8e93;
    line-height: 1.4;
}

.smd-hint--warn {
    color: #d97706;
}

html.dark .smd-label,
html.midnight .smd-label { color: #EAE6E1; }
html.cream .smd-label { color: #3D3832; }

html.dark .smd-hint,
html.midnight .smd-hint { color: #9A9CAE; }
html.cream .smd-hint { color: #847C72; }

html.dark .smd-hint--warn,
html.midnight .smd-hint--warn { color: #FBBF24; }

/* Segment */
.smd-segment {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0;
    padding: 3px;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 9px;
}

.smd-segment-btn {
    border: none;
    background: transparent;
    color: #8e8e93;
    font-size: 12.5px;
    font-weight: 600;
    font-family: inherit;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.18s, color 0.18s, box-shadow 0.18s;
}

.smd-segment-btn:hover { color: #1c1c1e; }

.smd-segment-btn--active {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.smd-segment-btn--in.smd-segment-btn--active { color: #28a745; }
.smd-segment-btn--out.smd-segment-btn--active { color: #ef4444; }
.smd-segment-btn--adj.smd-segment-btn--active { color: #d97706; }

html.dark .smd-segment,
html.midnight .smd-segment {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.08);
}

html.dark .smd-segment-btn,
html.midnight .smd-segment-btn { color: #9A9CAE; }

html.dark .smd-segment-btn:hover,
html.midnight .smd-segment-btn:hover { color: #EAE6E1; }

html.dark .smd-segment-btn--active,
html.midnight .smd-segment-btn--active {
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

html.dark .smd-segment-btn--in.smd-segment-btn--active,
html.midnight .smd-segment-btn--in.smd-segment-btn--active { color: #34D399; }
html.dark .smd-segment-btn--out.smd-segment-btn--active,
html.midnight .smd-segment-btn--out.smd-segment-btn--active { color: #F87171; }
html.dark .smd-segment-btn--adj.smd-segment-btn--active,
html.midnight .smd-segment-btn--adj.smd-segment-btn--active { color: #FBBF24; }

html.cream .smd-segment {
    background: rgba(61, 56, 50, 0.05);
    border-color: rgba(61, 56, 50, 0.1);
}

html.cream .smd-segment-btn { color: #847C72; }
html.cream .smd-segment-btn:hover { color: #3D3832; }
html.cream .smd-segment-btn--active { background: #FEFCF8; }
html.cream .smd-segment-btn--in.smd-segment-btn--active { color: #059669; }

/* Toggle */
.smd-toggle-wrap {
    display: inline-flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    width: 100%;
    padding: 10px 12px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 9px;
}

.smd-toggle-wrap input { display: none; }

.smd-toggle-pill {
    flex-shrink: 0;
    margin-top: 2px;
    width: 34px;
    height: 20px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
    position: relative;
    transition: background 0.2s;
}

.smd-toggle-pill::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.18s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.smd-toggle-wrap input:checked + .smd-toggle-pill {
    background: var(--panel-accent, #007AFF);
}

.smd-toggle-wrap input:checked + .smd-toggle-pill::after {
    transform: translateX(14px);
}

.smd-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.smd-toggle-title {
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
}

.smd-toggle-hint {
    font-size: 11px;
    color: #8e8e93;
    line-height: 1.4;
}

html.dark .smd-toggle-wrap,
html.midnight .smd-toggle-wrap {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
}

html.dark .smd-toggle-title,
html.midnight .smd-toggle-title { color: #EAE6E1; }

html.dark .smd-toggle-hint,
html.midnight .smd-toggle-hint { color: #9A9CAE; }

html.cream .smd-toggle-wrap {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
}

html.cream .smd-toggle-title { color: #3D3832; }
html.cream .smd-toggle-hint { color: #847C72; }

/* Modal cancel button — Metronic btn-light tum temalarda override */
.btn.btn-light,
.btn.btn-light:hover,
.btn.btn-light:focus,
.btn.btn-light:active,
.btn.btn-light:focus-visible {
    border-color: rgba(0, 0, 0, 0.08) !important;
    color: #1c1c1e !important;
    box-shadow: none !important;
}

.btn.btn-light,
.btn.btn-light:focus { background-color: rgba(0, 0, 0, 0.05) !important; }
.btn.btn-light:hover,
.btn.btn-light:active { background-color: rgba(0, 0, 0, 0.09) !important; }

html.dark .btn.btn-light,
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:focus,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:focus,
html.midnight .btn.btn-light:active {
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: #EAE6E1 !important;
}

html.dark .btn.btn-light,
html.dark .btn.btn-light:focus,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:focus { background-color: rgba(255, 255, 255, 0.07) !important; }
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:active { background-color: rgba(255, 255, 255, 0.13) !important; }

html.cream .btn.btn-light,
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:focus,
html.cream .btn.btn-light:active {
    border-color: rgba(61, 56, 50, 0.1) !important;
    color: #3D3832 !important;
}

html.cream .btn.btn-light,
html.cream .btn.btn-light:focus { background-color: rgba(61, 56, 50, 0.06) !important; }
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:active { background-color: rgba(61, 56, 50, 0.1) !important; }

</style>
